import React, { useMemo } from "react";
import { Cell, Pie, PieChart, Tooltip } from "recharts";
import { PieChart as PieIcon } from "lucide-react";
import type { OverviewLifecycleSlice } from "../../../types/dashboard";
import ChartBox from "./ChartBox";
import styles from "../Dashboard.module.scss";

const COLORS: Record<string, string> = {
  projected: "#94a3b8",
  inSetup: "#f59e0b",
  registrationOpen: "#1670bf",
  closing: "#ea580c",
  completed: "#16a34a",
};

export default function LifecycleChart({
  slices,
}: {
  slices: OverviewLifecycleSlice[];
}): JSX.Element {
  const data = useMemo(
    () => (slices || []).filter((s) => Number(s.count) > 0),
    [slices],
  );
  const total = data.reduce((sum, s) => sum + Number(s.count || 0), 0);

  return (
    <section className={styles.panel}>
      <div className={styles.panelHeader}>
        <div className={styles.panelTitleWrap}>
          <span className={styles.panelGlyph}>
            <PieIcon size={16} />
          </span>
          <div>
            <h3 className={styles.panelTitle}>Retreat Pipeline</h3>
            <p className={styles.panelKicker}>Where every retreat sits right now</p>
          </div>
        </div>
      </div>
      {total === 0 ? (
        <div className={styles.empty}>
          <p className="mb-0">No active retreats to chart yet.</p>
        </div>
      ) : (
        <>
          <div className={styles.donutWrap}>
            <ChartBox height={230}>
              <PieChart>
                <Pie
                  data={data}
                  dataKey="count"
                  nameKey="label"
                  innerRadius={68}
                  outerRadius={92}
                  paddingAngle={3}
                  stroke="#ffffff"
                  strokeWidth={3}
                  isAnimationActive={false}
                >
                  {data.map((slice) => (
                    <Cell key={slice.key} fill={COLORS[slice.key] || "#6fb0f9"} />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(value: any, name: any) => [Number(value).toLocaleString(), name]}
                />
              </PieChart>
            </ChartBox>
            <div className={styles.donutCenter}>
              <span className={styles.donutValue}>{total}</span>
              <span className={styles.donutLabel}>Active Retreats</span>
            </div>
          </div>
          <div className={styles.legend}>
            {data.map((slice) => (
              <span key={slice.key} className={styles.legendChip}>
                <span
                  className={styles.legendDot}
                  style={{ background: COLORS[slice.key] || "#6fb0f9" }}
                />
                {slice.label} · {slice.count}
              </span>
            ))}
          </div>
        </>
      )}
    </section>
  );
}
